import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import { API_URL } from '../../config';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import SuperAdminShell from './SuperAdminShell';
import PlatformOrgSubscriptionEditor from './PlatformOrgSubscriptionEditor';

const LIMIT = 50;

export default function SuperAdminOrganizationsPage() {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [listVersion, setListVersion] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setSkip(0);
  }, [debouncedQ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}/platform/organizations`, {
        params: { q: debouncedQ || undefined, skip, limit: LIMIT },
      })
      .then((res) => {
        if (cancelled) return;
        setTotal(res.data.total);
        setItems(res.data.items || []);
        const ids = (res.data.items || []).map((o) => o.id);
        setSelectedId((prev) => (prev && ids.includes(prev) ? prev : null));
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load organizations');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQ, skip, listVersion]);

  const onSaved = () => setListVersion((v) => v + 1);

  const page = Math.floor(skip / LIMIT) + 1;
  const lastPage = Math.max(1, Math.ceil(total / LIMIT) || 1);

  return (
    <SuperAdminShell
      title="Organizations"
      description="Search customer workspaces, open a row to adjust plan, billing cycle, status, and AI add-on."
      testId="super-admin-organizations"
    >
      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardHeader className="space-y-3">
            <CardTitle className="font-[Manrope] text-base">Directory</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Search by name, slug, or id…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <p className="text-xs text-slate-500">
              {total.toLocaleString()} total · page {page} of {lastPage}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <div className="flex items-center gap-2 text-slate-500 py-8">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : items.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No organizations match.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Plan</TableHead>
                    <TableHead className="hidden md:table-cell">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={selectedId === row.id ? 'selected' : undefined}
                      className="cursor-pointer"
                      onClick={() => setSelectedId(row.id)}
                    >
                      <TableCell>
                        <div className="font-medium text-slate-900 dark:text-white">{row.name}</div>
                        <div className="text-xs text-slate-500 font-mono truncate max-w-[200px]">
                          {row.slug}
                        </div>
                        {row.ai_addon_enabled ? (
                          <Badge className="mt-1 bg-emerald-600 text-[10px]">AI</Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {row.plan_id || '—'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {row.subscription_status || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="flex items-center justify-between gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={skip <= 0 || loading}
                onClick={() => setSkip((s) => Math.max(0, s - LIMIT))}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={skip + LIMIT >= total || loading}
                onClick={() => setSkip((s) => s + LIMIT)}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <PlatformOrgSubscriptionEditor organizationId={selectedId} onSaved={onSaved} />
      </div>
    </SuperAdminShell>
  );
}
